import {
  getMultiFactorResolver,
  multiFactor,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { getClientForPortal } from './clients';
import { endSession } from './endSession';
import { getFirebaseAuth } from './firebaseAuth';
import { HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from './httpStatus';
import { getPortalConfig, portalFromPath } from './portals';

export const SESSION_STATUS = {
  INITIALIZING: 'initializing',
  SIGNED_OUT: 'signedOut',
  READY: 'ready',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
};

let state = { status: SESSION_STATUS.INITIALIZING };
let listeners = [];
let started = false;
let hadUser = false;
let signingOutQuietly = false;
let loginNotice = null;
let currentPortal = null;
let markRestored;
const restored = new Promise((resolve) => {
  markRestored = resolve;
});

function setState(nextState) {
  state = nextState;
  listeners.slice().forEach((listener) => listener(state));
}

export function getSessionState() {
  return state;
}

export function subscribeToSession(listener) {
  listeners.push(listener);

  return () => {
    listeners = listeners.filter((existing) => existing !== listener);
  };
}

function handleIdTokenChange(user) {
  markRestored();

  if (user) {
    hadUser = true;
    return;
  }

  setState({ status: SESSION_STATUS.SIGNED_OUT });

  // A user who was signed in and now isn't (e.g. signed out in another tab)
  // must leave; someone who never signed in is left to the Auth Gate.
  if (hadUser && !signingOutQuietly) {
    endSession(currentPortal || portalFromPath(window.location.pathname));
  }
}

export function startSession() {
  const auth = getFirebaseAuth();

  if (started || !auth) {
    return;
  }
  started = true;

  onIdTokenChanged(auth, handleIdTokenChange);
}

function lacksRequiredSecondFactor(portal, user) {
  return (
    getPortalConfig(portal).mfaRequired &&
    multiFactor(user).enrolledFactors.length === 0
  );
}

// Loads the signed-in user's profile for a Portal once Firebase has restored
// the Session, and settles the session state.
export function loadProfile(portal) {
  currentPortal = portal;
  startSession();

  return restored.then(() => {
    const user = getFirebaseAuth().currentUser;

    if (!user) {
      setState({ status: SESSION_STATUS.SIGNED_OUT });
      return undefined;
    }

    // A Session without a required Second Factor is not a signed-in Session
    // (ADR 0005): a reload mid-enrolment, or a password-only Session left over
    // from before MFA, ends here instead of opening the Auth Gate.
    if (lacksRequiredSecondFactor(portal, user)) {
      setState({ status: SESSION_STATUS.SIGNED_OUT });
      endSession(portal);
      return undefined;
    }

    return getClientForPortal(portal)
      .get(getPortalConfig(portal).mePath)
      .then((response) => {
        setState({
          status: SESSION_STATUS.READY,
          portal,
          profile: response.data,
        });
      })
      .catch((error) => {
        if (error.status === HTTP_UNAUTHORIZED) {
          // The client has already ended the Session.
          setState({ status: SESSION_STATUS.SIGNED_OUT });
        } else if (error.status === HTTP_FORBIDDEN) {
          setState({ status: SESSION_STATUS.DENIED, portal });
        } else {
          setState({ status: SESSION_STATUS.UNAVAILABLE, portal });
        }
      });
  });
}

export const SIGN_IN_STATUS = {
  SIGNED_IN: 'signedIn',
  MFA_CHALLENGE: 'mfaChallenge',
  MFA_ENROLMENT: 'mfaEnrolment',
  MFA_ENROLLED: 'mfaEnrolled',
  MFA_REMOVED: 'mfaRemoved',
  MFA_SECRET: 'mfaSecret',
  FAILED: 'failed',
};

// Why a user was sent to the login page after a sign-out the page expected.
export const LOGIN_NOTICE = {
  ENROLLED: 'enrolled',
  REMOVED: 'removed',
};

export const SIGN_IN_FAILURE = {
  INVALID_CREDENTIALS: 'invalidCredentials',
  INVALID_CODE: 'invalidCode',
  CODE_EXPIRED: 'codeExpired',
  CHALLENGE_EXPIRED: 'challengeExpired',
  TOO_MANY_ATTEMPTS: 'tooManyAttempts',
  ACCESS_DENIED: 'accessDenied',
  UNAVAILABLE: 'unavailable',
};

const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required';
const TOTP_FACTOR_ID = 'totp';
const AUTHENTICATOR_DISPLAY_NAME = 'Authenticator app';

// The second step of a sign-in that is waiting for a code. It lives here, not
// in the login page, so the page never holds anything Firebase-shaped.
let pendingChallenge = null;

// The same for a user who must enrol a Second Factor: `secret` appears once
// enrolment has started.
let pendingEnrolment = null;

function failed(reason) {
  return { status: SIGN_IN_STATUS.FAILED, reason };
}

function outcomeFromSession(session) {
  if (session.status === SESSION_STATUS.READY) {
    return { status: SIGN_IN_STATUS.SIGNED_IN, profile: session.profile };
  }

  return failed(
    session.status === SESSION_STATUS.DENIED
      ? SIGN_IN_FAILURE.ACCESS_DENIED
      : SIGN_IN_FAILURE.UNAVAILABLE
  );
}

function outcomeFromError(error) {
  return failed(
    error?.code === 'auth/too-many-requests'
      ? SIGN_IN_FAILURE.TOO_MANY_ATTEMPTS
      : SIGN_IN_FAILURE.INVALID_CREDENTIALS
  );
}

function outcomeFromCodeError(error) {
  switch (error?.code) {
    case 'auth/too-many-requests':
    case 'auth/quota-exceeded':
      return failed(SIGN_IN_FAILURE.TOO_MANY_ATTEMPTS);
    case 'auth/network-request-failed':
      return failed(SIGN_IN_FAILURE.UNAVAILABLE);
    case 'auth/code-expired':
      return failed(SIGN_IN_FAILURE.CODE_EXPIRED);
    case 'auth/invalid-multi-factor-session':
    case 'auth/requires-recent-login':
      pendingChallenge = null;
      pendingEnrolment = null;
      return failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED);
    default:
      return failed(SIGN_IN_FAILURE.INVALID_CODE);
  }
}

function startChallenge(portal, error) {
  const resolver = getMultiFactorResolver(getFirebaseAuth(), error);
  const hint = resolver.hints.find(
    ({ factorId }) => factorId === TOTP_FACTOR_ID
  );

  if (!hint) {
    return failed(SIGN_IN_FAILURE.UNAVAILABLE);
  }

  pendingChallenge = { portal, resolver, hint };
  return { status: SIGN_IN_STATUS.MFA_CHALLENGE };
}

function settleSignedIn(portal) {
  return loadProfile(portal).then(
    () => outcomeFromSession(getSessionState()),
    () => failed(SIGN_IN_FAILURE.UNAVAILABLE)
  );
}

// Signs a user in and settles the session for the Portal they signed in to.
// Always resolves an outcome telling the login page what to show: signed in
// with the profile, waiting for the code of a Second Factor, or failed with a
// reason (wrong credentials, too many attempts, refused by the Portal, or the
// backend unavailable).
export function signIn(portal, email, password) {
  startSession();
  pendingChallenge = null;
  pendingEnrolment = null;

  return signInWithEmailAndPassword(getFirebaseAuth(), email, password)
    .then(() => {
      if (lacksRequiredSecondFactor(portal, getFirebaseAuth().currentUser)) {
        pendingEnrolment = { portal };
        return { status: SIGN_IN_STATUS.MFA_ENROLMENT };
      }

      return settleSignedIn(portal);
    })
    .catch((error) =>
      error?.code === MFA_REQUIRED_CODE
        ? startChallenge(portal, error)
        : outcomeFromError(error)
    );
}

// Answers the challenge started by `signIn` with the 6-digit code. A wrong
// code keeps the challenge open so the user can try again.
export function completeMfaChallenge(code) {
  if (!pendingChallenge) {
    return Promise.resolve(failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED));
  }

  const { portal, resolver, hint } = pendingChallenge;
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);

  return resolver.resolveSignIn(assertion).then(() => {
    pendingChallenge = null;
    return settleSignedIn(portal);
  }, outcomeFromCodeError);
}

// Drops a challenge or enrolment the user walked away from (back to the login
// form, or off the page).
export function cancelMfaChallenge() {
  pendingChallenge = null;
  pendingEnrolment = null;
}

// Begins enrolling a Second Factor, for a user `signIn` sent to enrolment or for
// one who is signed in and chose to set it up: resolves the secret and the QR
// code content for their authenticator app.
export function startMfaEnrolment() {
  const user = getFirebaseAuth().currentUser;
  const signedInPortal =
    state.status === SESSION_STATUS.READY ? state.portal : null;
  const portal = pendingEnrolment ? pendingEnrolment.portal : signedInPortal;

  if (!portal || !user) {
    return Promise.resolve(failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED));
  }

  return multiFactor(user)
    .getSession()
    .then((session) => TotpMultiFactorGenerator.generateSecret(session))
    .then((secret) => {
      pendingEnrolment = { portal, secret };

      return {
        status: SIGN_IN_STATUS.MFA_SECRET,
        secretKey: secret.secretKey,
        qrCodeUrl: secret.generateQrCodeUrl(
          user.email,
          getPortalConfig(portal).mfaIssuer
        ),
      };
    })
    .catch((error) =>
      error?.code === 'auth/requires-recent-login'
        ? failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED)
        : failed(SIGN_IN_FAILURE.UNAVAILABLE)
    );
}

// Enrolment ends with a sign-out, not a Session: the token the user holds was
// issued before the second factor existed, and the code they just entered can
// not be used twice, so the next sign-in has to start again with a new code.
// The sign-out is expected, so it does not send the user away from the page.
function signOutQuietly(portal, notice) {
  signingOutQuietly = true;

  return signOut(getFirebaseAuth())
    .catch(() => {})
    .then(() => {
      signingOutQuietly = false;
      hadUser = false;
      loginNotice = { portal, notice };
    });
}

function signOutAfterEnrolment(portal) {
  return signOutQuietly(portal, LOGIN_NOTICE.ENROLLED).then(() => ({
    status: SIGN_IN_STATUS.MFA_ENROLLED,
  }));
}

// Finishes enrolment with the first code from the authenticator app. A wrong
// code keeps enrolment open so the user can try again. With `keepSession` the user stays
// signed in (the caller must then call `finishMfaEnrolment` or leave it).
export function completeMfaEnrolment(code, { keepSession = false } = {}) {
  const user = getFirebaseAuth().currentUser;

  if (!pendingEnrolment || !pendingEnrolment.secret || !user) {
    return Promise.resolve(failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED));
  }

  const { portal, secret } = pendingEnrolment;
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    secret,
    code
  );

  return multiFactor(user)
    .enroll(assertion, AUTHENTICATOR_DISPLAY_NAME)
    .then(() => {
      pendingEnrolment = null;
      return keepSession
        ? { status: SIGN_IN_STATUS.MFA_ENROLLED }
        : signOutAfterEnrolment(portal);
    }, outcomeFromCodeError);
}

// Ends the Session of a user who enrolled with `keepSession`, once the page has
// told them what happens next. They land on the Portal's login page with a
// notice, like after any enrolment.
export function finishMfaEnrolment(portal) {
  return signOutAfterEnrolment(portal);
}

let profileInFlight = null;

// Makes sure the session has been settled for this Portal, asking the backend
// only when it hasn't been (or was unavailable last time). Callers asking at
// the same time share one request.
export function ensureProfile(portal) {
  const settledForPortal =
    state.portal === portal &&
    (state.status === SESSION_STATUS.READY ||
      state.status === SESSION_STATUS.DENIED);

  if (settledForPortal) {
    return Promise.resolve();
  }

  if (!profileInFlight || profileInFlight.portal !== portal) {
    const clear = () => {
      profileInFlight = null;
    };
    profileInFlight = {
      portal,
      promise: loadProfile(portal).then(clear, clear),
    };
  }

  return profileInFlight.promise;
}

// Removes every Second Factor of a signed-in user and ends the Session.
// Firebase signs the user out itself when the last factor goes, but not
// always, so this does it too.
function removeAllSecondFactors(portal, user) {
  const factors = multiFactor(user);
  // Signed out here, quietly, so the login page can say why they are back.
  const removed = () =>
    signOutQuietly(portal, LOGIN_NOTICE.REMOVED).then(() => ({
      status: SIGN_IN_STATUS.MFA_REMOVED,
    }));

  return factors.enrolledFactors
    .slice()
    .reduce(
      (done, factor) => done.then(() => factors.unenroll(factor)),
      Promise.resolve()
    )
    .then(removed, (error) => {
      switch (error?.code) {
        case 'auth/user-token-expired':
          return removed();
        case 'auth/requires-recent-login':
          return failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED);
        default:
          return failed(SIGN_IN_FAILURE.UNAVAILABLE);
      }
    });
}

// Removes the Second Factors of the user who is signed in right now (the
// settings page). Firebase asks for a recent sign-in first; when it does, the
// outcome says the sign-in has expired.
export function unenrollAllSecondFactors() {
  const user = getFirebaseAuth().currentUser;

  if (!user) {
    return Promise.resolve(failed(SIGN_IN_FAILURE.CHALLENGE_EXPIRED));
  }

  return removeAllSecondFactors(
    currentPortal || portalFromPath(window.location.pathname),
    user
  );
}

// Whether the user who is signed in right now has a Second Factor.
export function hasSecondFactor() {
  const user = getFirebaseAuth().currentUser;

  return !!user && multiFactor(user).enrolledFactors.length > 0;
}

// Why the user of this Portal was just signed out by the page (an enrolment or a
// removal of their Second Factor), once, so the login page can say so (also when
// the Auth Gate sent them there). Another Portal's login page leaves it alone.
export function takeLoginNotice(portal) {
  if (!loginNotice || loginNotice.portal !== portal) {
    return null;
  }

  const { notice } = loginNotice;

  loginNotice = null;
  return notice;
}
